/**
 * Memorial Wall - Modular Entry System
 *
 * Central assembly point for all memorial entries.
 * Positioning is calculated by the positioning module.
 */

import {
  CATEGORIES,
  SECTIONS,
  calculatePositions,
  groupEntriesBySection
} from './positioning.js';

// ============================================
// FAMILY ENTRIES
// ============================================
import { kedemFamilyMemorial } from './families/kedem-family.js';
import { kutzFamilyMemorial } from './families/kutz-family.js';
import { bibasFamilyMemorial } from './families/bibas-family.js';
import { sharabiFamilyMemorial } from './families/sharabi-family.js';
import { orAndEinavLevyMemorial } from './families/or-and-einav-levy.js';

// ============================================
// YOUNG LIVES ENTRIES
// ============================================
import { jakeMarloweMemorial } from './young-lives/jake-marlowe.js';
import { shaniLoukMemorial } from './young-lives/shani-louk.js';
import { hershGoldbergpolinMemorial } from './young-lives/hersh-goldberg-polin.js';
import { naorHasidimandsivanElkabetsMemorial } from './young-lives/naor-hasidim-and-sivan-elkabets.js';
import { yazanAbuJamaaMemorial } from './young-lives/yazan-abu-jamaa.js';
import { anerShapiraMemorial } from './young-lives/aner-shapira.js';
import { edenYerushalmiMemorial } from './young-lives/eden-yerushalmi.js';
import { alexanderLobanovMemorial } from './young-lives/alexander-lobanov.js';
import { almogSarusiMemorial } from './young-lives/almog-sarusi.js';
import { yagevBuchshtabMemorial } from './young-lives/yagev-buchshtab.js';

// ============================================
// COMMUNITY LEADERS ENTRIES
// ============================================
import { ofirLiebsteinMemorial } from './community-leaders/ofir-liebstein.js';
import { vivianSilverMemorial } from './community-leaders/vivian-silver.js';
import { odedLifshitzMemorial } from './community-leaders/oded-lifshitz.js';
import { carmelGatMemorial } from './community-leaders/carmel-gat.js';
import { masterSgtOriDaninoMemorial } from './community-leaders/master-sgt-ori-danino.js';
import { abrahamMunderMemorial } from './community-leaders/abraham-munder.js';
import { chaimPeriMemorial } from './community-leaders/chaim-peri.js';
import { yoramMetzgerMemorial } from './community-leaders/yoram-metzger.js';
import { alexanderDancygMemorial } from './community-leaders/alexander-dancyg.js';
import { nadavPopplewellMemorial } from './community-leaders/nadav-popplewell.js';

// ============================================
// HOSTAGES RETURNED DECEASED (2025 Ceasefire)
// ============================================
import { aryehZalmanovichMemorial } from './hostages-returned/aryeh-zalmanovich.js';
import { tamirAdarMemorial } from './hostages-returned/tamir-adar.js';
import { menyGodardMemorial } from './hostages-returned/meny-godard.js';
import { ziyadneFamilyMemorial } from './hostages-returned/ziyadne-family.js';
import { itzhakElgaratMemorial } from './hostages-returned/itzhak-elgarat.js';
import { tsachiIdanMemorial } from './hostages-returned/tsachi-idan.js';
import { shlomoMansourMemorial } from './hostages-returned/shlomo-mansour.js';
import { ohadYahalomiMemorial } from './hostages-returned/ohad-yahalomi.js';
import { guyIluzMemorial } from './hostages-returned/guy-iluz.js';
import { ronenEngelMemorial } from './hostages-returned/ronen-engel.js';
import { inbarHaimanMemorial } from './hostages-returned/inbar-haiman.js';
import { drorOrMemorial } from './hostages-returned/dror-or.js';
import { asafHamamiMemorial } from './hostages-returned/asaf-hamami.js';
import { omerNeutraMemorial } from './hostages-returned/omer-neutra.js';
import { ozDanielMemorial } from './hostages-returned/oz-daniel.js';
import { itayChenMemorial } from './hostages-returned/itay-chen.js';
import { danielPeretzMemorial } from './hostages-returned/daniel-peretz.js';
import { ranGviliMemorial } from './hostages-returned/ran-gvili.js';

// ============================================
// FULL MEMORIAL LINK
// ============================================
import { fullMemorialMemorial, FULL_MEMORIAL_URL } from './special/full-memorial.js';

// ============================================
// GROUP ENTRIES BY CATEGORY (raw, no positions)
// ============================================

const familyEntries = [
  kedemFamilyMemorial,
  kutzFamilyMemorial,
  bibasFamilyMemorial,
  sharabiFamilyMemorial,
  orAndEinavLevyMemorial,
];

const youngLivesEntries = [
  jakeMarloweMemorial,
  shaniLoukMemorial,
  hershGoldbergpolinMemorial,
  naorHasidimandsivanElkabetsMemorial,
  yazanAbuJamaaMemorial,
  anerShapiraMemorial,
  edenYerushalmiMemorial,
  alexanderLobanovMemorial,
  almogSarusiMemorial,
  yagevBuchshtabMemorial,
];

const communityLeadersEntries = [
  ofirLiebsteinMemorial,
  vivianSilverMemorial,
  odedLifshitzMemorial,
  carmelGatMemorial,
  masterSgtOriDaninoMemorial,
  abrahamMunderMemorial,
  chaimPeriMemorial,
  yoramMetzgerMemorial,
  alexanderDancygMemorial,
  nadavPopplewellMemorial,
];

const hostagesReturnedEntries = [
  aryehZalmanovichMemorial,
  tamirAdarMemorial,
  menyGodardMemorial,
  ziyadneFamilyMemorial,
  itzhakElgaratMemorial,
  tsachiIdanMemorial,
  shlomoMansourMemorial,
  ohadYahalomiMemorial,
  guyIluzMemorial,
  ronenEngelMemorial,
  inbarHaimanMemorial,
  drorOrMemorial,
  asafHamamiMemorial,
  omerNeutraMemorial,
  ozDanielMemorial,
  itayChenMemorial,
  danielPeretzMemorial,
  ranGviliMemorial,
];

// ============================================
// APPLY POSITIONING ALGORITHM
// ============================================

// Desktop positions
const positionedFamilies = calculatePositions(familyEntries, CATEGORIES.FAMILIES, 'desktop');
const positionedYoungLives = calculatePositions(youngLivesEntries, CATEGORIES.YOUNG_LIVES, 'desktop');
const positionedCommunityLeaders = calculatePositions(communityLeadersEntries, CATEGORIES.COMMUNITY_LEADERS, 'desktop');
const positionedHostagesReturned = calculatePositions(hostagesReturnedEntries, CATEGORIES.HOSTAGES_RETURNED, 'desktop');
const positionedFullMemorial = calculatePositions([fullMemorialMemorial], CATEGORIES.FULL_MEMORIAL, 'desktop');

// Mobile positions
const mobilePositionedFamilies = calculatePositions(familyEntries, CATEGORIES.FAMILIES, 'mobile');
const mobilePositionedYoungLives = calculatePositions(youngLivesEntries, CATEGORIES.YOUNG_LIVES, 'mobile');
const mobilePositionedCommunityLeaders = calculatePositions(communityLeadersEntries, CATEGORIES.COMMUNITY_LEADERS, 'mobile');
const mobilePositionedHostagesReturned = calculatePositions(hostagesReturnedEntries, CATEGORIES.HOSTAGES_RETURNED, 'mobile');
const mobilePositionedFullMemorial = calculatePositions([fullMemorialMemorial], CATEGORIES.FULL_MEMORIAL, 'mobile');

// ============================================
// ASSEMBLED LAYOUTS
// ============================================

export const memorialsDesktop = [
  ...positionedFamilies,
  ...positionedYoungLives,
  ...positionedCommunityLeaders,
  ...positionedHostagesReturned,
  ...positionedFullMemorial,
];

export const memorialsMobile = [
  ...mobilePositionedFamilies,
  ...mobilePositionedYoungLives,
  ...mobilePositionedCommunityLeaders,
  ...mobilePositionedHostagesReturned,
  ...mobilePositionedFullMemorial,
];

// ============================================
// SECTION CONFIGURATION (for MemorialWall)
// ============================================

// Map section IDs to their entry IDs for the UI
export const sectionConfig = {
  'section-1': {
    title: 'Remembering Families',
    entryIds: familyEntries.map(e => e.id)
  },
  'section-2': {
    title: 'Young Lives Lost',
    entryIds: youngLivesEntries.map(e => e.id)
  },
  'section-3': {
    title: 'Community Leaders',
    entryIds: [...communityLeadersEntries.map(e => e.id), fullMemorialMemorial.id]
  },
  'section-4': {
    title: 'Hostages Returned',
    entryIds: hostagesReturnedEntries.map(e => e.id)
  }
};

// ============================================
// EXPORTS
// ============================================

export { CATEGORIES, SECTIONS, FULL_MEMORIAL_URL };

export default {
  memorialsDesktop,
  memorialsMobile,
  sectionConfig,
  CATEGORIES,
  SECTIONS,
  FULL_MEMORIAL_URL
};
